import {
  type EffortLevel,
  type ImpactLevel,
  LEVEL_LABELS,
} from "@/bizminer/App/PillarInsights/ImprovementLevers/shared/improvementLeverUtils.ts";
import MetricCard from "@/components/ui/MetricCard";
import type {
  BizminerImprovementLever,
  BizminerPillarInsight,
} from "@/types/serializers";
import ImprovementLeverLayout from "./_components/ImprovementLeverLayout";

interface OverviewProps {
  pillar_insight: BizminerPillarInsight;
  improvement_lever: BizminerImprovementLever;
  current_tab: string;
}

const IMPACT_COLORS: Record<ImpactLevel, string> = {
  high: "high",
  medium: "medium",
  low: "low",
};

const EFFORT_COLORS: Record<EffortLevel, string> = {
  low: "high",
  medium: "medium",
  high: "low",
};

export default function Overview({
  pillar_insight,
  improvement_lever,
  current_tab,
}: OverviewProps) {
  const formatCurrency = (
    profit:
      | { value: number; currency: string; description?: string }
      | null
      | undefined,
  ) => {
    if (!profit || !profit.value) return "$0";

    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: profit.currency || "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(profit.value);
    } catch (_error) {
      return `$${profit.value?.toLocaleString() || "0"}`;
    }
  };
  return (
    <ImprovementLeverLayout
      pillar_insight={pillar_insight}
      improvement_lever={improvement_lever}
      current_tab={current_tab}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Metrics */}
        <div className="lg:col-span-1">
          <div className="space-y-4">
            <MetricCard
              label="Validation Score"
              value={improvement_lever.validation_score || "N/A"}
              info="AI-generated validation score based on multiple factors"
            />

            <MetricCard
              label="Impact"
              value={LEVEL_LABELS[improvement_lever.impact as ImpactLevel]}
              color={IMPACT_COLORS[improvement_lever.impact as ImpactLevel]}
            />

            <MetricCard
              label="Effort"
              value={LEVEL_LABELS[improvement_lever.effort as EffortLevel]}
              color={EFFORT_COLORS[improvement_lever.effort as EffortLevel]}
            />

            <MetricCard
              label="Additional Profit"
              value={formatCurrency(improvement_lever.additional_profit)}
            />

            <MetricCard
              label="Improvement Type"
              value={improvement_lever.improvement_type || "N/A"}
            />

            <MetricCard
              label="Prospected Closing Period"
              value={improvement_lever.closing_period || "N/A"}
            />
          </div>
        </div>

        {/* Right Column - Content */}
        <div className="lg:col-span-2">
          <div className="space-y-8">
            {/* Pain Point & Solution */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Pain Point */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-card-foreground mb-4">
                  Pain Point
                </h3>
                <div className="text-sm text-muted-foreground whitespace-pre-line">
                  {improvement_lever.pain_point?.description ||
                    "No pain point specified"}
                </div>
              </div>

              {/* Solution */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-card-foreground mb-4">
                  Solution
                </h3>
                <div className="text-sm text-muted-foreground whitespace-pre-line">
                  {improvement_lever.solution?.description ||
                    "No solution specified"}
                </div>
              </div>
            </div>

            {/* Bottom Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Affected Products */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-card-foreground mb-4">
                  Affected Products
                </h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {improvement_lever.affected_products &&
                  improvement_lever.affected_products.length > 0 ? (
                    improvement_lever.affected_products.map(
                      (product, index) => <li key={index}>{product}</li>,
                    )
                  ) : (
                    <li>No affected products specified</li>
                  )}
                </ul>
              </div>

              {/* Functions to Engage */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-card-foreground mb-4">
                  Functions to Engage
                </h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {improvement_lever.functions_to_engage &&
                  improvement_lever.functions_to_engage.length > 0 ? (
                    improvement_lever.functions_to_engage.map((func, index) => (
                      <li key={index}>{func}</li>
                    ))
                  ) : (
                    <li>No functions to engage specified</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ImprovementLeverLayout>
  );
}
